import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { searchApolloLeads } from '@ndi/leads/api/leads.js';
import { ApolloLeadSearchInput } from '@ndi/leads/baseql/apollo-lead-search.js';
import { html, nothing } from 'lit';
import { ConnectionNoteNotice } from '../connection-note-notice.js';
import { toast } from '../toast.js';
import './prompt-editor.js';
import type { PromptEditorElement } from './prompt-editor.js';
import type { DelayUnit, OfferingConfig, SequenceChannel, SequenceStep } from './types.js';
import {
  buildPreviewRequestBody,
  connectStepCarriesANote,
  DEFAULT_PROMPTS,
  defaultEmailSequence,
  defaultLinkedinSequence,
  defaultMultiSequence,
  OUTREACH_LANGUAGES,
  SENDER_CHIP_THEME,
  SENDER_VARIABLES,
  SEQUENCE_NEEDS_EMAIL_ACCOUNT,
  SEQUENCE_NEEDS_LINKEDIN_ACCOUNT,
  TEMPLATE_VARIABLES,
  VARIABLE_CHIP_THEME,
} from './types.js';
import type { LeadRecord } from './wiz-leads.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface WizSequenceProps {
  sequence: SequenceStep[];
  leads: LeadRecord[];
  offering: OfferingConfig;
  onSequenceChange: (sequence: SequenceStep[]) => void;
  autoReply?: boolean;
  onAutoReplyChange?: (enabled: boolean) => void;
  replyPrompt?: string;
  onReplyPromptChange?: (prompt: string) => void;
  /** Whether any assigned Sales Representative has an email account connected. */
  hasEmailAccount?: boolean;
  /** Whether any assigned Sales Representative has a LinkedIn account connected. */
  hasLinkedinAccount?: boolean;
  /**
   * The LinkedIn accounts this draft actually sends from, for the connection-
   * note notice. Not derivable here: `hasLinkedinAccount` answers whether the
   * reps are covered, which is a different question from WHICH accounts they
   * cover it with, and the notice names accounts.
   */
  linkedinAccountIds?: readonly string[];
  /**
   * The campaign's selected Sales Rep (see `previewSenderId`), passed to the
   * message preview so it signs off as that rep. Without it the preview fell
   * back to the user's default rep — and to "Sarah Miller" when none was set.
   */
  previewAiEmployeeId?: string;
  /**
   * Apollo sourcing criteria of the Leads step. When no leads were uploaded,
   * one matching lead is fetched so message previews use a REAL person
   * instead of “Hi there” placeholder copy.
   */
  apolloSearchInput?: Record<string, unknown>;
  /** Outreach language (ISO code, see OUTREACH_LANGUAGES). Default 'en'. */
  language?: string;
  onLanguageChange?: (language: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DELAY_UNITS: { value: DelayUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const CHANNELS_ALL: { value: SequenceChannel; label: string }[] = [
  { value: 'email', label: '✉️ Email' },
  { value: 'linkedin_connect', label: '🔗 LinkedIn Connect' },
  { value: 'linkedin_dm', label: '💬 LinkedIn DM' },
];

// ---------------------------------------------------------------------------
// Channel presentation metadata (drives the lane layout + compact cards).
// Colors map to the NDI brand tokens: email=primary, linkedin_connect=secondary,
// linkedin_dm=accent (same convention used across campaign-detail.ts).
// ---------------------------------------------------------------------------
type Lane = 'email' | 'linkedin';
interface ChannelMeta {
  lane: Lane;
  label: string;
  previewLabel: string;
  suggestedLabel: string;
  icon: string;
  /** solid number badge */
  badge: string;
  /** tinted channel chip */
  chip: string;
}

const CHANNEL_META: Record<SequenceChannel, ChannelMeta> = {
  email: {
    lane: 'email',
    label: 'Email',
    previewLabel: 'Email Message',
    suggestedLabel: 'Suggested Email',
    icon: 'icon-[material-symbols--mail-outline]',
    badge: 'bg-primary text-primary-content',
    chip: 'bg-primary/10 text-primary',
  },
  linkedin_connect: {
    lane: 'linkedin',
    label: 'LinkedIn Connect',
    previewLabel: 'LinkedIn Request Message',
    suggestedLabel: 'Suggested Connection Note',
    icon: 'icon-[material-symbols--person-add-outline]',
    badge: 'bg-secondary text-secondary-content',
    chip: 'bg-secondary/10 text-secondary',
  },
  linkedin_dm: {
    lane: 'linkedin',
    label: 'LinkedIn DM',
    previewLabel: 'LinkedIn Direct Message',
    suggestedLabel: 'Suggested LinkedIn Message',
    icon: 'icon-[material-symbols--chat-outline]',
    badge: 'bg-accent text-accent-content',
    chip: 'bg-accent/10 text-accent',
  },
};

const laneOf = (c: SequenceChannel): Lane => CHANNEL_META[c].lane;

// Kept in sync with LINKEDIN_CONNECT_NOTE_MAX_CHARS in
// entrypoints/sdr/src/server/helpers/message-generation.ts (the server-side
// source of truth — a different package, so it can't be imported directly).
// Live testing showed Unipile rejects connection-request invites with HTTP
// 400 errors/too_many_characters even at exactly 300 chars (a figure widely
// quoted for LinkedIn's web UI) — 200 is Unipile's own documented cap.
export const LINKEDIN_CONNECT_NOTE_MAX_CHARS = 200;

const ORDINAL_SUFFIX = ['th', 'st', 'nd', 'rd'];
/** 1 → "1st", 2 → "2nd", 3 → "3rd", 4 → "4th"… */
const ordinal = (n: number): string => {
  const v = n % 100;
  return `${n}${ORDINAL_SUFFIX[(v - 20) % 10] ?? ORDINAL_SUFFIX[v] ?? ORDINAL_SUFFIX[0]}`;
};

export interface StepDescriptor {
  /** 1-based position within the step's own lane (Email #1,#2 … / LinkedIn #1,#2 …). */
  number: number;
  /** Position-aware chip label: "Intro Email", "1st Follow-up", "Connect", "Message". */
  label: string;
}

/** Per-lane numbering + AiSDR-style position labels for every step. */
function describeSteps(seq: SequenceStep[]): StepDescriptor[] {
  let e = 0;
  let l = 0;
  return seq.map(s => {
    if (laneOf(s.channel) === 'email') {
      const n = e++;
      return { number: n + 1, label: n === 0 ? 'Intro Email' : `${ordinal(n)} Follow-up` };
    }
    const n = l++;
    return { number: n + 1, label: s.channel === 'linkedin_connect' ? 'Connect' : 'Message' };
  });
}

/** Singular/plural-aware unit word for the “Wait …” chip. */
const unitWord = (v: number, u: DelayUnit): string => (v === 1 ? u.replace(/s$/, '') : u);

// ---------------------------------------------------------------------------
// Variable bar
// ---------------------------------------------------------------------------

/**
 * Renders a horizontal bar of draggable, clickable variable chips.
 * `onInsert` receives just the variable key (e.g. "first_name").
 */
function renderVariableChip(
  v: { key: string; label: string },
  onInsert: (key: string) => void,
  theme: { bg: string; color: string; border: string },
) {
  return html`
    <span
      class="cursor-pointer rounded-md px-2 py-0.5 text-xs font-medium transition-colors select-none"
      style="background:${theme.bg}; color:${theme.color}; border:${theme.border};"
      draggable="true"
      title="Click to insert or drag into prompt"
      @dragstart=${(e: DragEvent) => {
        e.dataTransfer?.setData('text/plain', `{{${v.key}}}`);
        e.dataTransfer!.effectAllowed = 'copy';
      }}
      @click=${() => onInsert(v.key)}
      >${v.label}</span
    >
  `;
}

function renderVariableBar(onInsert: (key: string) => void) {
  return html`
    <div class="flex flex-wrap items-center gap-1.5 py-1">
      <span class="text-base-content/40 text-xs select-none">Variables:</span>
      ${TEMPLATE_VARIABLES.map(v => renderVariableChip(v, onInsert, VARIABLE_CHIP_THEME))}
      <span class="text-base-content/40 ml-2 text-xs select-none">Sender:</span>
      ${SENDER_VARIABLES.map(v => renderVariableChip(v, onInsert, SENDER_CHIP_THEME))}
    </div>
  `;
}

const channelsForType = () => CHANNELS_ALL;

/**
 * Conditions are derived automatically (no per-step UI):
 *  • the very first step always runs ("always");
 *  • a LinkedIn DM needs an accepted connection first ("accepted");
 *  • every other follow-up only runs if the lead has NOT replied yet ("no_reply").
 * A reply on any channel stops the rest of the sequence, so "no_reply" is the
 * natural guard for everything after step 1.
 */
function normalizeConditions(steps: SequenceStep[]): SequenceStep[] {
  return steps.map((s, i) => {
    const condition: SequenceStep['condition'] =
      i === 0 ? 'always' : s.channel === 'linkedin_dm' ? 'accepted' : 'no_reply';
    return s.condition === condition ? s : { ...s, condition };
  });
}

// ---------------------------------------------------------------------------
// Per-step example generation (always-on preview)
// ---------------------------------------------------------------------------

interface GeneratedExample {
  subject: string;
  body: string;
}

/** Minimal lead facts used to personalize the step-card preview. */
export interface SampleLead {
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  industry?: string;
  email?: string;
}

// Generated samples are persisted on the step itself (previewSubject /
// previewBody / previewKey) so the card survives collapsing, wizard
// navigation and re-opening the campaign without a fresh LLM call.
// `previewKey` fingerprints the content the sample was generated from — when
// it no longer matches (prompt refined, channel switched, offering edited)
// the stored sample is stale and a new one is generated. "Regenerate" forces
// a fresh sample even for unchanged content.
const contentHash = (value: string): string => {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
const previewContentKey = (step: SequenceStep, offering: OfferingConfig): string =>
  contentHash(
    JSON.stringify([
      step.prompt,
      step.subject,
      step.subjectPrompt ?? '',
      step.channel,
      offering.name,
      offering.description,
      offering.talkingPoints,
    ]),
  );

/** Render a realistic sample of this step's message via the backend LLM. */
async function generateExample(
  step: SequenceStep,
  offering: OfferingConfig,
  sampleLead?: SampleLead | null,
  language?: string,
  aiEmployeeId?: string,
): Promise<GeneratedExample> {
  if (!step.prompt.trim()) return { subject: '', body: '' };
  // A refine instruction typed under the preview keeps steering every
  // regeneration (incl. language switches) — not just the explicit Generate
  // click that rewrote the prompt.
  const instruction = step.refineInstruction?.trim();
  const effectivePrompt = instruction
    ? `${step.prompt}\n\nADDITIONAL INSTRUCTION (must be followed): ${instruction}`
    : step.prompt;
  // Personalize the preview with a real lead (uploaded or Apollo-sourced).
  // Obfuscated Apollo last names ("Bo***n") are skipped — fallbacks read better.
  const lead: Record<string, string> = {};
  if (sampleLead) {
    if (sampleLead.firstName) lead.first_name = sampleLead.firstName;
    if (sampleLead.lastName && !sampleLead.lastName.includes('*')) lead.last_name = sampleLead.lastName;
    if (sampleLead.company) lead.company = sampleLead.company;
    if (sampleLead.jobTitle) lead.job_title = sampleLead.jobTitle;
    if (sampleLead.industry) lead.industry = sampleLead.industry;
    if (sampleLead.email) lead.email = sampleLead.email;
  }
  const res = await fetch('/api/prompt/test', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      buildPreviewRequestBody({ step, offering, effectivePrompt, lead, language, aiEmployeeId }),
    ),
  });
  const data = (await res.json()) as { ok: boolean; subject?: string; body?: string; error?: string };
  if (!data.ok) throw new Error(data.error ?? 'Could not generate a preview.');
  return { subject: (data.subject ?? '').trim(), body: (data.body ?? '').trim() };
}

// ---------------------------------------------------------------------------
// SequenceStepCard — one step (compact or expanded). Owns its own always-on
// preview generation via useQuery so every step shows a real sample message,
// and edits happen purely through AI actions (no raw prompt / variables shown).
// ---------------------------------------------------------------------------

interface StepCardProps {
  step: SequenceStep;
  descriptor: StepDescriptor;
  expanded: boolean;
  isFirst: boolean;
  isLast: boolean;
  offering: OfferingConfig;
  /** Real lead used to personalize the preview (null while none is available). */
  sampleLead: SampleLead | null;
  /** True while a sample lead is still being fetched from Apollo. */
  sampleLeadLoading: boolean;
  /** Outreach language (ISO code) the previews are generated in. */
  language?: string;
  /** The draft's LinkedIn accounts, so the note notice names only those. */
  linkedinAccountIds?: readonly string[];
  /** The campaign's selected rep, so the preview signs off as that rep. */
  previewAiEmployeeId?: string;
  onToggle: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onPatch: (patch: Partial<SequenceStep>) => void;
}

const SequenceStepCard = stateful((props: StepCardProps) => {
  const {
    step,
    descriptor,
    expanded,
    isFirst,
    isLast,
    offering,
    sampleLead,
    sampleLeadLoading,
    language,
    previewAiEmployeeId,
    onToggle,
    onRemove,
    onMoveUp,
    onMoveDown,
    onPatch,
  } = props;
  const meta = CHANNEL_META[step.channel];
  const isEmail = step.channel === 'email';
  const isConnect = step.channel === 'linkedin_connect';

  const [nonce, setNonce] = useState(0);
  const [improving, setImproving] = useState<'body' | 'subject' | null>(null);
  // The refine instruction lives on the step (not in component state) so it
  // stays visible across wizard navigation and re-opening the campaign.
  const instruction = step.refineInstruction ?? '';
  // Overlay showing the full refine instruction (long prompts don't fit the inline field).
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  // True once an AI refinement (a prompt, Shorten or Make-it-friendly) has been
  // applied to the current sample, so the preview can be visibly greyed as a
  // "this was refined" cue. A full Regenerate clears it — a fresh sample is not
  // a refinement. Local state on purpose: it is a just-refined hint, not a fact
  // that must survive navigation.
  const [refinementApplied, setRefinementApplied] = useState(false);
  // Manual edit mode — lets the user rewrite the ACTUAL message text (sent as
  // written, variables interpolated per lead) instead of only steering the AI.
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const isManual = !!step.manualBody?.trim();

  // Serve the sample persisted on the step. It only regenerates when the user
  // explicitly triggers it (Generate/Shorten/Friendly/Regenerate), not on
  // wizard navigation or by simply re-opening an existing campaign draft.
  // `sampleLeadKey` only matters for the FIRST generation of a brand-new step:
  // it defers that call until the sample lead finished loading so the very
  // first preview is already personalized with a real person.
  const sampleLeadKey = sampleLead
    ? `${sampleLead.firstName ?? ''}|${sampleLead.company ?? ''}`
    : sampleLeadLoading
      ? 'loading'
      : 'none';
  const contentKey = previewContentKey(step, offering);
  const preview = useQuery<GeneratedExample>(async () => {
    // Manual mode renders step.manualBody directly — never call the LLM.
    if (isManual) return { subject: step.previewSubject ?? '', body: step.previewBody ?? '' };
    if (nonce === 0 && step.previewBody) {
      return { subject: step.previewSubject ?? '', body: step.previewBody };
    }
    // No stored preview yet and the sample lead is still loading — hold off so
    // the first generated sample already uses the real lead.
    if (nonce === 0 && sampleLeadLoading) return { subject: '', body: '' };
    const fresh = await generateExample(step, offering, sampleLead, language, previewAiEmployeeId);
    if (fresh.subject || fresh.body) {
      onPatch({ previewSubject: fresh.subject, previewBody: fresh.body, previewKey: contentKey });
    }
    return fresh;
  }, [step.id, isManual, nonce, sampleLeadKey, language ?? 'en', previewAiEmployeeId ?? '']);
  const busy = improving !== null || preview.loading;

  const improve = async (mode: 'shorten' | 'friendly' | 'instruction' | 'subject', freeInstruction?: string) => {
    setImproving(mode === 'subject' ? 'subject' : 'body');
    try {
      const res = await fetch('/api/prompt/improve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: mode === 'subject' ? step.subject : step.prompt,
          mode,
          instruction: mode === 'subject' ? step.prompt : freeInstruction,
          channel: step.channel,
        }),
      });
      const data = (await res.json()) as { ok: boolean; text?: string; error?: string };
      if (data.ok && data.text) {
        onPatch(mode === 'subject' ? { subject: data.text } : { prompt: data.text });
        // A body refinement (prompt / Shorten / Friendly) greys the preview; a
        // subject-only tweak leaves the message body as-is, so it does not.
        if (mode !== 'subject') setRefinementApplied(true);
        // Regenerate preview only after an explicit user action.
        setNonce(nonce + 1);
      } else {
        toast.error(data.error ?? 'AI request failed. Please try again.');
      }
    } catch {
      toast.error('Network error — could not reach the AI service.');
    } finally {
      setImproving(null);
    }
  };

  const runInstruction = () => {
    const text = instruction.trim();
    if (!text) {
      toast.info('Describe the change you want, then press Generate.');
      return;
    }
    // Keep the instruction in the input so the user still sees what they asked
    // for after the message regenerates.
    void improve('instruction', text);
  };

  const skeletonLines = (n: number) => html`
    <div class="flex flex-col gap-2">
      ${Array.from({ length: n }).map(() => html`<div class="skeleton h-3.5 w-full"></div>`)}
      <div class="skeleton h-3.5 w-2/3"></div>
    </div>
  `;

  // ── Compact ────────────────────────────────────────────────
  if (!expanded) {
    // Manual messages are shown as written; AI steps show the stored sample.
    const body = step.manualBody?.trim() || (preview.value?.body?.trim() ?? '');
    return html`
      <div
        class="card bg-base-100 border-base-300 cursor-pointer border shadow-sm transition-shadow hover:shadow-md"
        @click=${onToggle}
      >
        <div class="card-body gap-2 p-3">
          <div class="flex items-center gap-2">
            <span
              class="${meta.badge} flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold"
            >
              #${descriptor.number}
            </span>
            <span
              class="${meta.chip} inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide uppercase"
            >
              <span class="${meta.icon} text-sm"></span>
              ${descriptor.label}
            </span>
            <div class="flex-1"></div>
            <button
              class="btn btn-ghost btn-xs btn-square text-base-content/40 hover:text-error"
              title="Delete step"
              @click=${(e: Event) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <span class="icon-[material-symbols--delete-outline] text-base"></span>
            </button>
            <span class="icon-[material-symbols--expand-more] text-base-content/40 text-base"></span>
          </div>
          <div class="text-base-content/50 text-[0.7rem] font-medium tracking-wide uppercase">${meta.previewLabel}:</div>
          ${
            body
              ? html`<p class="text-base-content/80 line-clamp-3 text-sm whitespace-pre-wrap">${body}</p>`
              : preview.loading
                ? skeletonLines(2)
                : preview.error
                  ? html`<p class="text-error/80 text-sm">Could not generate preview — open to retry.</p>`
                  : html`<p class="text-base-content/40 text-sm italic">No preview yet.</p>`
          }
        </div>
      </div>
    `;
  }

  // ── Expanded ───────────────────────────────────────────────
  return html`
    <div class="card bg-base-100 border-primary/30 border-2 shadow-md">
      <div class="card-body flex flex-col gap-4 p-4">
        <!-- header -->
        <div class="flex items-center gap-2">
          <span
            class="${meta.badge} flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 text-sm font-bold"
          >
            #${descriptor.number}
          </span>
          <span
            class="${meta.chip} inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold tracking-wide uppercase"
          >
            <span class="${meta.icon} text-sm"></span>
            ${descriptor.label}
          </span>
          <div class="flex-1"></div>
          <button class="btn btn-ghost btn-xs btn-square" ?disabled=${isFirst} @click=${onMoveUp} title="Move up">
            <span class="icon-[material-symbols--arrow-upward] text-base"></span>
          </button>
          <button class="btn btn-ghost btn-xs btn-square" ?disabled=${isLast} @click=${onMoveDown} title="Move down">
            <span class="icon-[material-symbols--arrow-downward] text-base"></span>
          </button>
          <button
            class="btn btn-ghost btn-xs btn-square text-base-content/50 hover:text-error"
            @click=${onRemove}
            title="Delete step"
          >
            <span class="icon-[material-symbols--delete-outline] text-base"></span>
          </button>
          <button class="btn btn-ghost btn-xs btn-square" @click=${onToggle} title="Collapse">
            <span class="icon-[material-symbols--expand-less] text-base"></span>
          </button>
        </div>

        <!-- suggested header + regenerate + manual edit -->
        <div class="flex items-center gap-2">
          <span class="${meta.icon} text-base-content/60 text-base"></span>
          <span class="text-sm font-semibold">${meta.suggestedLabel}</span>
          ${
            isManual
              ? html`<span class="badge badge-secondary badge-sm gap-1">
                  <span class="icon-[material-symbols--edit] text-xs"></span> Manual
                </span>`
              : nothing
          }
          <div class="flex-1"></div>
          ${
            editing
              ? nothing
              : html`
                <button
                  class="btn btn-ghost btn-xs gap-1"
                  ?disabled=${busy}
                  title="Rewrite the message text yourself — it will be sent exactly as you write it"
                  @click=${() => {
                    setEditSubject(isManual ? step.subject : preview.value?.subject || step.subject);
                    setEditBody(step.manualBody || preview.value?.body || '');
                    setEditing(true);
                  }}
                >
                  <span class="icon-[material-symbols--edit] text-sm"></span>
                  Edit message
                </button>
                ${
                  isManual
                    ? html`
                      <button
                        class="btn btn-ghost btn-xs text-primary gap-1"
                        title="Discard the manual text and let the AI personalize each lead's message again"
                        @click=${() => onPatch({ manualBody: undefined })}
                      >
                        <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
                        Back to AI
                      </button>
                    `
                    : html`
                      <button
                        class="btn btn-ghost btn-xs text-primary gap-1"
                        ?disabled=${busy}
                        title="Generate a fresh sample"
                        @click=${() => {
                          // Full regenerate resets the ad-hoc refine input and the
                          // "refined" grey — this produces a fresh, un-refined sample.
                          onPatch({ refineInstruction: '' });
                          setRefinementApplied(false);
                          setNonce(nonce + 1);
                        }}
                      >
                        ${
                          preview.loading && improving === null
                            ? html`<span class="loading loading-spinner loading-xs"></span>`
                            : html`<span class="icon-[material-symbols--autorenew] text-sm"></span>`
                        }
                        Regenerate
                      </button>
                    `
                }
              `
          }
        </div>

        <!--
          Where this text actually lands, on every connect step.

          This was a one-line grey hint and has been raised to a full notice, at
          the customer's request: it was too quiet for the only screen where the
          person can still act on it. Still an INFO alert, never a warning or an
          error — nothing here is a mistake the user made, and an error state
          would be a lie about whose problem this is.

          Shown only when all three hold: a connect step, a note actually
          written in it, and a sending account that is not a paid seat. The
          third is the component's to judge; the first two are ours, because
          only this card knows the step. See connection-note-notice.ts for the
          three reasons it distinguishes, why it carries no number, and why it
          does not claim the note is re-sent after acceptance.
        -->
        ${
          isConnect
            ? ConnectionNoteNotice({
              hasNoteOnConnectStep: connectStepCarriesANote(step),
              // Scoped to this draft's accounts. Absent while the Sales Rep
              // step is unanswered, and then the notice renders nothing rather
              // than naming every colleague in the workspace.
              sendingAccountIds: props.linkedinAccountIds ?? [],
            })
            : nothing
        }

        ${
          editing
            ? html`
              <!-- manual edit mode -->
              <div class="border-secondary/30 bg-secondary/5 flex flex-col gap-3 rounded-xl border p-3">
                ${
                  isEmail
                    ? html`
                      <div class="flex flex-col gap-1">
                        <span class="text-xs font-medium">Subject</span>
                        <input
                          type="text"
                          class="input input-bordered input-sm w-full"
                          .value=${editSubject}
                          @input=${(e: Event) => setEditSubject((e.target as HTMLInputElement).value)}
                        />
                      </div>
                    `
                    : nothing
                }
                <div class="flex flex-col gap-1">
                  <div class="flex items-center justify-between">
                    <span class="text-xs font-medium">Message</span>
                    ${
                      isConnect
                        ? html`<span
                            class="text-xs ${editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS ? 'text-error font-semibold' : 'text-base-content/50'}"
                          >
                            ${editBody.length} / ${LINKEDIN_CONNECT_NOTE_MAX_CHARS}
                          </span>`
                        : nothing
                    }
                  </div>
                  <p class="text-base-content/50 text-xs">
                    This exact text is sent to every lead — no AI rewriting. Variables like
                    <code class="bg-base-200 rounded px-1">{{first_name}}</code> are replaced with each lead's real
                    data.
                  </p>
                  ${
                    isConnect && editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS
                      ? html`
                        <div class="alert alert-error py-2 text-sm">
                          <span class="icon-[material-symbols--warning-outline] text-lg"></span>
                          <span>
                            Too many characters — LinkedIn connection requests are limited to
                            ${LINKEDIN_CONNECT_NOTE_MAX_CHARS} characters. Shorten the message to continue.
                          </span>
                        </div>
                      `
                      : nothing
                  }
                  <prompt-editor
                    data-editor-id="step-body-${step.id}"
                    .value=${editBody}
                    .rows=${8}
                    @value-changed=${(e: CustomEvent<string>) => setEditBody(e.detail ?? '')}
                  ></prompt-editor>
                  ${renderVariableBar(key => {
                    const el = document.querySelector(
                      `[data-editor-id="step-body-${step.id}"]`,
                    ) as PromptEditorElement | null;
                    el?.insertVariable(key);
                  })}
                </div>
                <div class="flex justify-end gap-2">
                  <button class="btn btn-ghost btn-sm" @click=${() => setEditing(false)}>Cancel</button>
                  <button
                    class="btn btn-primary btn-sm gap-1"
                    ?disabled=${!editBody.trim() || (isEmail && !editSubject.trim()) || (isConnect && editBody.length > LINKEDIN_CONNECT_NOTE_MAX_CHARS)}
                    @click=${() => {
                      onPatch({ manualBody: editBody, subject: editSubject });
                      setEditing(false);
                    }}
                  >
                    <span class="icon-[material-symbols--check] text-sm"></span>
                    Save message
                  </button>
                </div>
              </div>
            `
            : html`
        <!-- subject (email only) -->
        ${
          isEmail
            ? html`
              <div class="border-base-300 bg-base-200/40 flex items-center gap-2 rounded-lg border px-3 py-2">
                <span class="text-base-content/50 text-xs font-medium">Subject</span>
                ${
                  isManual
                    ? html`<span class="flex-1 text-sm font-medium">${step.subject || '—'}</span>`
                    : preview.loading
                      ? html`<div class="skeleton h-4 flex-1"></div>`
                      : html`<span class="flex-1 text-sm font-medium">${preview.value?.subject || '—'}</span>`
                }
              </div>
            `
            : nothing
        }

        <!-- generated message -->
        <div class="border-base-300 relative rounded-lg border p-4">
          <div class="text-primary mb-2 flex items-center gap-1.5 text-xs font-semibold">
            <span class="${isManual ? 'icon-[material-symbols--edit]' : 'icon-[material-symbols--auto-awesome]'} text-sm"></span>
            ${isManual ? 'Manual message — sent as written' : 'AI-suggested message'}
            ${
              // A quiet marker that the shown message is the result of a refine,
              // paired with the greyed body below so the grey is not read as
              // "stale" or "disabled". Cleared by a full Regenerate.
              !isManual && refinementApplied
                ? html`<span class="text-base-content/50 inline-flex items-center gap-0.5 font-normal">
                    <span class="icon-[material-symbols--check-small] text-sm"></span>Refined
                  </span>`
                : nothing
            }
          </div>
          ${
            isManual
              ? html`<div class="text-base-content/90 text-sm leading-relaxed whitespace-pre-wrap">${step.manualBody}</div>`
              : preview.loading
                ? skeletonLines(5)
                : preview.error
                  ? html`<p class="text-error text-sm">Could not generate this message. Try “Regenerate”.</p>`
                  : html`<div
                      class="${refinementApplied ? 'text-base-content/60' : 'text-base-content/90'} text-sm leading-relaxed whitespace-pre-wrap transition-colors"
                    >${preview.value?.body || 'No message generated yet.'}</div>`
          }
          <p class="text-base-content/50 border-base-300 mt-3 border-t pt-2 text-xs">
            <span class="icon-[material-symbols--info-outline] align-text-bottom text-sm"></span>
            ${
              isManual
                ? html`This text is sent exactly as written — only variables like {{first_name}} are replaced with each
                  lead's real data.`
                : sampleLead?.firstName
                  ? html`Preview personalized with a real lead from your list: <strong>${sampleLead.firstName}${sampleLead.company ? ` (${sampleLead.company})` : ''}</strong>. Every lead gets their own personalized version.`
                  : html`Every lead gets their own personalized version with their real name, company, and job title.`
            }
          </p>
          ${
            improving === 'body'
              ? html`<div
                  class="bg-base-100/70 absolute inset-0 flex items-center justify-center gap-2 rounded-lg text-sm backdrop-blur-[1px]"
                >
                  <span class="loading loading-spinner loading-sm"></span> Updating…
                </div>`
              : nothing
          }
        </div>

        <!-- improve with AI (not applicable while a manual message is set) -->
        ${
          isManual
            ? nothing
            : html`
        <div class="border-primary/20 bg-primary/5 flex flex-col gap-2 rounded-xl border p-3">
          <div class="flex items-start gap-2">
            <div class="relative flex-1">
              <textarea
                class="textarea textarea-bordered textarea-sm max-h-40 min-h-9 w-full resize-none overflow-y-auto py-1.5 leading-snug"
                rows="1"
                placeholder="Make changes to the content through prompts!"
                .value=${instruction}
                ?disabled=${busy}
                @input=${(e: Event) => {
                  const el = e.target as HTMLTextAreaElement;
                  // Auto-grow with the content (capped by max-h, then scrolls).
                  el.style.height = 'auto';
                  el.style.height = `${el.scrollHeight}px`;
                  onPatch({ refineInstruction: el.value });
                }}
                @keydown=${(e: KeyboardEvent) => {
                  // Enter runs the prompt; Shift+Enter inserts a newline.
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    runInstruction();
                  }
                }}
              ></textarea>
            </div>
            <button class="btn btn-primary btn-sm gap-1 shrink-0" ?disabled=${busy} @click=${runInstruction}>
              <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
              Generate
            </button>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button class="btn btn-outline btn-xs gap-1" ?disabled=${busy} @click=${() => improve('shorten')}>
              <span class="icon-[material-symbols--compress] text-sm"></span>
              Shorten
            </button>
            <button class="btn btn-outline btn-xs gap-1" ?disabled=${busy} @click=${() => improve('friendly')}>
              <span class="icon-[material-symbols--sentiment-satisfied-outline] text-sm"></span>
              Make it friendly
            </button>
            ${
              instruction.length > 150
                ? html`
                  <button
                    class="btn btn-ghost btn-xs text-primary ml-auto gap-1"
                    title="Show the full prompt in an overlay"
                    @click=${() => setShowFullPrompt(true)}
                  >
                    <span class="icon-[material-symbols--open-in-full] text-sm"></span>
                    Expand prompt
                  </button>
                `
                : nothing
            }
          </div>
        </div>
        ${
          showFullPrompt
            ? html`
              <div
                class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                @click=${() => setShowFullPrompt(false)}
              >
                <div
                  class="bg-base-100 flex max-h-[80vh] w-full max-w-2xl flex-col gap-3 rounded-2xl p-5 shadow-xl"
                  @click=${(e: Event) => e.stopPropagation()}
                >
                  <div class="flex items-center gap-2">
                    <span class="icon-[material-symbols--auto-awesome] text-primary text-lg"></span>
                    <h3 class="text-base font-semibold">Refine prompt</h3>
                    <div class="flex-1"></div>
                    <button class="btn btn-ghost btn-xs btn-square" @click=${() => setShowFullPrompt(false)}>
                      <span class="icon-[material-symbols--close] text-base"></span>
                    </button>
                  </div>
                  <textarea
                    class="textarea textarea-bordered min-h-48 w-full flex-1 text-sm leading-relaxed"
                    .value=${instruction}
                    ?disabled=${busy}
                    @input=${(e: Event) => onPatch({ refineInstruction: (e.target as HTMLTextAreaElement).value })}
                  ></textarea>
                  <div class="flex justify-end gap-2">
                    <button class="btn btn-ghost btn-sm" @click=${() => setShowFullPrompt(false)}>Close</button>
                    <button
                      class="btn btn-primary btn-sm gap-1"
                      ?disabled=${busy}
                      @click=${() => {
                        setShowFullPrompt(false);
                        runInstruction();
                      }}
                    >
                      <span class="icon-[material-symbols--auto-awesome] text-sm"></span>
                      Generate
                    </button>
                  </div>
                </div>
              </div>
            `
            : nothing
        }
        `
        }
        `
        }
      </div>
    </div>
  `;
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const WizSequence = stateful((props: WizSequenceProps) => {
  const {
    sequence,
    leads,
    offering,
    onSequenceChange,
    autoReply,
    onAutoReplyChange,
    replyPrompt,
    onReplyPromptChange,
  } = props;
  const hasEmailAccount = props.hasEmailAccount ?? true;
  const hasLinkedinAccount = props.hasLinkedinAccount ?? true;

  // Local UI state
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
  const [editingDelayIdx, setEditingDelayIdx] = useState<number | null>(null);
  const [insertMenuAt, setInsertMenuAt] = useState<number | null>(null);

  // ── Sample lead for realistic previews ─────────────────────────────
  // Message previews should read like the real thing — “Hi Ariaan” instead of
  // “Hi there”. Prefer the first uploaded/imported lead; when the user sourced
  // via Apollo instead, fetch one matching lead from the saved criteria.
  const uploadedSample: SampleLead | null =
    leads.length > 0
      ? {
          firstName: leads[0]?.firstName,
          lastName: leads[0]?.lastName,
          company: leads[0]?.company,
          jobTitle: leads[0]?.jobTitle,
          industry: leads[0]?.industry,
        }
      : null;
  const apolloCriteria = !uploadedSample && props.apolloSearchInput ? props.apolloSearchInput : null;
  const apolloSampleQuery = useQuery<SampleLead | null>(async () => {
    if (!apolloCriteria) return null;
    try {
      const entity = new ApolloLeadSearchInput({});
      const src = apolloCriteria as Record<string, unknown>;
      const str = (v: unknown): string => (typeof v === 'string' ? v : '');
      const arr = (v: unknown): string[] =>
        Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
      // Fields still holding exactly what the ICP interpretation auto-filled
      // (draft `autoFilled` snapshot) are stripped, mirroring the lead-preview
      // search — the mapper re-derives them fresh from the ICP text.
      const auto = (src.autoFilled && typeof src.autoFilled === 'object' ? src.autoFilled : {}) as Record<
        string,
        unknown
      >;
      const stale = (key: string): boolean =>
        auto[key] !== undefined && JSON.stringify(src[key]) === JSON.stringify(auto[key]);
      entity.icpDescription = str(src.icpDescription);
      entity.companyLocation = stale('companyLocation') ? [] : arr(src.companyLocation);
      entity.companyIndustry = stale('companyIndustry') ? [] : arr(src.companyIndustry);
      entity.industryKeywords = stale('industryKeywords') ? '' : str(src.industryKeywords);
      entity.employeeCount = stale('employeeCount') ? [] : arr(src.employeeCount);
      entity.jobTitle = stale('jobTitle') ? '' : str(src.jobTitle);
      entity.department = stale('department') ? [] : arr(src.department);
      entity.personLocation = stale('personLocation') ? [] : arr(src.personLocation);
      entity.page = 1;
      entity.perPage = 1;
      const result = await searchApolloLeads(entity)({
        totalEntries: 1,
        leads: { apolloPersonId: 1, firstName: 1, lastName: 1, company: 1, jobTitle: 1, industry: 1 },
      });
      const lead = result?.leads?.[0];
      if (!lead) return null;
      return {
        firstName: lead.firstName,
        lastName: lead.lastName,
        company: lead.company,
        jobTitle: lead.jobTitle,
        industry: lead.industry,
      };
    } catch {
      return null; // preview falls back to generic copy — never block the step
    }
  }, [JSON.stringify(apolloCriteria ?? null)]);
  const sampleLead = uploadedSample ?? apolloSampleQuery.value ?? null;
  const sampleLeadLoading = !uploadedSample && !!apolloCriteria && apolloSampleQuery.loading;

  const descriptors = describeSteps(sequence);

  /** Single mutation path — conditions are always re-derived automatically. */
  const commit = (steps: SequenceStep[]) => onSequenceChange(normalizeConditions(steps));

  // Step cards patch asynchronously (a generated sample is written back onto
  // the step once the LLM responds). Those writebacks must never run through
  // a render-time closure — they would resurrect an old sequence and wipe
  // whatever happened while the request was in flight (other cards generating
  // in parallel, steps added or moved, instructions typed). `live` is a
  // stable ref that always points at the current render's sequence + commit.
  const [live] = useState({ sequence, commit });
  live.sequence = sequence;
  live.commit = commit;

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Patch one step by id against the LATEST sequence — safe for async callers. */
  const patchStep = (stepId: string, patch: Partial<SequenceStep>) => {
    live.commit(live.sequence.map(s => (s.id === stepId ? { ...s, ...patch } : s)));
  };

  const updateStep = (idx: number, patch: Partial<SequenceStep>) => {
    commit(sequence.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const makeStep = (channel: SequenceChannel, pos: number): SequenceStep => {
    const isFollowup = channel === 'email' && sequence.some(s => s.channel === 'email');
    const promptKey = channel === 'email' ? (isFollowup ? 'email_followup' : 'email') : channel;
    return {
      id: crypto.randomUUID(),
      channel,
      delayValue: pos === 0 ? 0 : channel === 'linkedin_dm' ? 15 : 2,
      delayUnit: pos === 0 ? 'hours' : channel === 'linkedin_dm' ? 'minutes' : 'days',
      // Placeholder — commit() re-derives the real condition for the final position.
      condition: 'no_reply',
      prompt: DEFAULT_PROMPTS[promptKey] ?? '',
      subject: channel === 'email' ? 'Quick question about {{company}}' : '',
    };
  };

  const insertStepAt = (pos: number, channel: SequenceChannel) => {
    const arr = [...sequence];
    arr.splice(pos, 0, makeStep(channel, pos));
    commit(arr);
    setInsertMenuAt(null);
    setExpandedIdx(pos); // auto-open the freshly added step
  };

  const addStep = (channel: SequenceChannel) => insertStepAt(sequence.length, channel);

  const removeStep = (idx: number) => {
    commit(sequence.filter((_, i) => i !== idx));
    if (expandedIdx === idx) setExpandedIdx(null);
    else if (expandedIdx !== null && expandedIdx > idx) setExpandedIdx(expandedIdx - 1);
  };

  const moveStep = (fromIdx: number, toIdx: number) => {
    if (toIdx < 0 || toIdx >= sequence.length) return;
    const arr = [...sequence];
    const [item] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, item!);
    commit(arr);
    setExpandedIdx(toIdx);
  };

  const toggleExpand = (idx: number) => {
    setExpandedIdx(expandedIdx === idx ? null : idx);
    setEditingDelayIdx(null);
    setInsertMenuAt(null);
  };

  // Validation warnings ----------------------------------------------------

  const warnings = (): string[] => {
    const w: string[] = [];
    if (sequence.length === 0) return w;

    if (sequence[0]!.delayValue > 0) {
      w.push('Step 1 has a delay — typically the first step starts immediately (delay 0).');
    }

    let hasConnect = false;
    for (const s of sequence) {
      if (s.channel === 'linkedin_connect') hasConnect = true;
      if (s.channel === 'linkedin_dm' && !hasConnect) {
        w.push('LinkedIn DM before a Connect step — the lead may not be connected yet.');
        break;
      }
    }

    for (let i = 0; i < sequence.length; i++) {
      if (sequence[i]!.condition === 'accepted') {
        const hasPriorConnect = sequence.slice(0, i).some(s => s.channel === 'linkedin_connect');
        if (!hasPriorConnect) {
          w.push(`Step ${i + 1} has "Accepted" condition but no prior LinkedIn Connect step.`);
        }
      }
    }

    // Multiple "Always" steps: once a lead replies on ANY channel the whole
    // outbound sequence stops automatically (there is no "if replied" step),
    // so a later "Always" step — e.g. an "Always" LinkedIn DM after an
    // "Always" email — will be SKIPPED for any lead who already replied.
    // Warn so the user knows the second "Always" only reaches non-repliers.
    const alwaysSteps = sequence.map((s, i) => ({ step: s, idx: i })).filter(({ step }) => step.condition === 'always');
    if (alwaysSteps.length > 1) {
      const stepList = alwaysSteps.map(s => s.idx + 1).join(', ');
      w.push(
        `Multiple "Always" steps (steps ${stepList}). A reply on any channel stops the rest of the ` +
          `sequence, so later "Always" steps are skipped for leads who already replied — they only ` +
          `reach leads who have NOT replied yet.`,
      );
    }

    return w;
  };

  // Hard blockers: a channel is used in the sequence but no matching account is
  // connected for the assigned Sales Representative(s). These must be resolved
  // before the campaign can continue, so they render as errors (not warnings).
  const accountErrors = (): string[] => {
    const e: string[] = [];
    const hasEmailStep = sequence.some(s => s.channel === 'email');
    const hasLinkedinStep = sequence.some(s => s.channel === 'linkedin_connect' || s.channel === 'linkedin_dm');
    if (hasEmailStep && !hasEmailAccount) {
      e.push(SEQUENCE_NEEDS_EMAIL_ACCOUNT);
    }
    if (hasLinkedinStep && !hasLinkedinAccount) {
      e.push(SEQUENCE_NEEDS_LINKEDIN_ACCOUNT);
    }
    return e;
  };

  // Render -----------------------------------------------------------------

  // ── Lane render helpers ──────────────────────────────────────────────────

  /** “+” affordance between/around steps; expands to a small channel menu. */
  const renderInsertZone = (pos: number) => {
    const open = insertMenuAt === pos;
    return html`
      <div class="relative z-10 flex flex-col items-center py-1.5">
        ${
          open
            ? html`
              <div class="bg-base-100 border-base-300 z-10 flex flex-col gap-1 rounded-xl border p-2 shadow-lg">
                <span class="text-base-content/50 px-2 pb-1 text-xs font-medium">Add a step here</span>
                ${CHANNELS_ALL.map(ch => {
                  const meta = CHANNEL_META[ch.value];
                  return html`
                    <button
                      class="hover:bg-base-200 flex items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm transition-colors"
                      @click=${() => insertStepAt(pos, ch.value)}
                    >
                      <span class="${meta.chip} flex h-6 w-6 items-center justify-center rounded-md">
                        <span class="${meta.icon} text-sm"></span>
                      </span>
                      ${meta.label}
                    </button>
                  `;
                })}
                <button
                  class="text-base-content/40 hover:text-base-content mt-0.5 px-3 py-1 text-left text-xs"
                  @click=${() => setInsertMenuAt(null)}
                >
                  Cancel
                </button>
              </div>
            `
            : html`
              <button
                class="btn btn-circle btn-xs border-base-300 bg-base-100 hover:bg-primary hover:text-primary-content hover:border-primary border"
                title="Add step here"
                @click=${() => {
                  setInsertMenuAt(pos);
                  setEditingDelayIdx(null);
                }}
              >
                <span class="icon-[material-symbols--add] text-base"></span>
              </button>
            `
        }
      </div>
    `;
  };

  /** “Wait N days” connector chip (delay BEFORE step i); click to edit inline. */
  const renderWaitChip = (i: number) => {
    const step = sequence[i];
    if (!step) return nothing;
    if (editingDelayIdx === i) {
      return html`
        <div class="relative z-10 flex items-center justify-center py-1.5">
          <div class="bg-base-100 border-base-300 flex items-center gap-2 rounded-full border px-3 py-1 shadow-sm">
            <span class="icon-[material-symbols--schedule] text-base-content/50 text-sm"></span>
            <span class="text-base-content/70 text-xs font-medium">Wait</span>
            <input
              type="number"
              min="0"
              max="9999"
              .value=${String(step.delayValue)}
              class="input input-xs w-14 text-center"
              @change=${(e: Event) =>
                updateStep(i, { delayValue: Math.max(0, Number((e.target as HTMLInputElement).value) || 0) })}
            />
            <select
              class="select select-xs"
              .value=${step.delayUnit}
              @change=${(e: Event) => updateStep(i, { delayUnit: (e.target as HTMLSelectElement).value as DelayUnit })}
            >
              <!--
                The selectedness is set on the OPTION, not left to the
                \`.value\` binding above. lit applies element properties before
                it commits child templates, so on the render that opens this
                editor \`.value\` is assigned while the option list is still
                empty — the browser drops it and falls back to the first option.
                The dropdown then reads "Minutes" for a step whose unit is
                "days", which is a control that lies about its own state.

                Reported 17 August: "I enter Wait 10 minutes, click OK, and it
                says 10 days". Nothing had gone wrong on save — the field had
                always said days, and the box showing Minutes never fired a
                change event because it was never actually changed.
              -->
              ${DELAY_UNITS.map(
                u => html`<option value=${u.value} .selected=${u.value === step.delayUnit}>${u.label}</option>`,
              )}
            </select>
            <button
              class="btn btn-xs btn-circle btn-ghost text-success"
              title="Done"
              @click=${() => setEditingDelayIdx(null)}
            >
              <span class="icon-[material-symbols--check] text-sm"></span>
            </button>
          </div>
        </div>
      `;
    }
    return html`
      <div class="relative z-10 flex items-center justify-center py-1.5">
        <button
          class="border-base-300 bg-base-100 text-base-content/70 hover:border-primary hover:text-primary inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium shadow-sm transition-colors"
          title="Edit the delay before this step"
          @click=${() => {
            setEditingDelayIdx(i);
            setInsertMenuAt(null);
          }}
        >
          <span class="icon-[material-symbols--schedule] text-sm"></span>
          Wait ${step.delayValue} ${unitWord(step.delayValue, step.delayUnit)}
        </button>
      </div>
    `;
  };

  const renderImmediatePill = () => html`
    <div class="relative z-10 flex items-center justify-center py-1.5">
      <span
        class="border-base-300 bg-base-100 text-base-content/50 inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium shadow-sm"
      >
        <span class="icon-[material-symbols--bolt] text-warning text-sm"></span>
        Starts immediately
      </span>
    </div>
  `;

  // Empty state
  if (sequence.length === 0) {
    return html`
      <div class="flex flex-col gap-6">
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body flex flex-col items-center gap-4 py-10 text-center">
            <span class="icon-[material-symbols--account-tree] text-base-content/20 text-5xl"></span>
            <div>
              <h2 class="card-title justify-center">Build Your Outreach Sequence</h2>
              <p class="text-base-content/70 mt-1 text-sm">
                Define the steps, timing, and AI prompts for your multi-channel outreach.
              </p>
            </div>

            <div class="mt-2 flex flex-wrap justify-center gap-3">
              <button
                class="btn btn-outline btn-primary gap-2"
                @click=${() => onSequenceChange(defaultEmailSequence())}
              >
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: Email (3 Steps)
              </button>
              <button
                class="btn btn-outline btn-secondary gap-2"
                @click=${() => onSequenceChange(defaultLinkedinSequence())}
              >
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: LinkedIn (2 Steps)
              </button>
              <button class="btn btn-outline btn-accent gap-2" @click=${() => onSequenceChange(defaultMultiSequence())}>
                <span class="icon-[material-symbols--bolt] text-lg"></span>
                Quick Start: Cross-Channel (4 Steps)
              </button>
            </div>

            <div class="divider text-base-content/40 text-xs">or add steps manually</div>

            <div class="flex flex-wrap gap-2">
              ${channelsForType().map(
                ch => html`
                  <button class="btn btn-sm btn-ghost gap-1" @click=${() => addStep(ch.value)}>
                    <span class="icon-[material-symbols--add] text-base"></span>
                    ${ch.label}
                  </button>
                `,
              )}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Main sequence view
  const warn = warnings();
  const acctErrors = accountErrors();

  return html`
    <div class="flex flex-col gap-4">
      <div class="flex items-center gap-3">
        <span class="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-lg">
          <span class="icon-[material-symbols--account-tree] text-xl"></span>
        </span>
        <div>
          <h2 class="text-xl font-semibold">Outreach Sequence</h2>
          <p class="text-base-content/70 text-sm">Every step auto-generates a sample message you can refine with AI.</p>
        </div>
        <div class="flex-1"></div>
        <label class="flex items-center gap-1.5" title="All messages, previews and auto-replies are written in this language">
          <span class="icon-[material-symbols--language] text-base-content/60 text-lg"></span>
          <select
            class="select select-bordered select-sm"
            .value=${props.language || 'en'}
            @change=${(e: Event) => {
              const next = (e.target as HTMLSelectElement).value;
              if (next === (props.language || 'en')) return;
              // The parent applies the language AND clears the stored previews
              // in ONE state update — two separate updates raced against each
              // other and occasionally lost one of the changes.
              props.onLanguageChange?.(next);
            }}
          >
            <!-- Same reason as the delay unit above: selectedness belongs on the option. -->
            ${OUTREACH_LANGUAGES.map(
              l => html`<option value=${l.code} .selected=${l.code === (props.language || 'en')}>${l.label}</option>`,
            )}
          </select>
        </label>
        <button
          class="btn btn-sm btn-ghost text-error gap-1"
          title="Remove all steps"
          @click=${() => {
            onSequenceChange([]);
            setExpandedIdx(null);
          }}
        >
          <span class="icon-[material-symbols--delete-outline] text-base"></span>
          Clear all
        </button>
      </div>

      <!-- Account blockers (must be fixed before continuing) -->
      ${
        acctErrors.length > 0
          ? html`
            <div class="alert alert-error text-sm">
              <span class="icon-[material-symbols--block] text-lg"></span>
              <div class="flex flex-col gap-0.5">
                ${acctErrors.map(e => html`<span class="font-medium">${e}</span>`)}
                <span class="text-xs opacity-80">
                  Go back to “Sales Representatives” and connect/select an account, or remove the affected step.
                </span>
              </div>
            </div>
          `
          : nothing
      }

      <!-- Warnings -->
      ${
        warn.length > 0
          ? html`
            <div class="alert alert-warning text-sm">
              <span class="icon-[material-symbols--warning] text-lg"></span>
              <div class="flex flex-col gap-0.5">${warn.map(w => html`<span>⚠ ${w}</span>`)}</div>
            </div>
          `
          : nothing
      }

      <!-- Sequence canvas — a single connected flow; a dashed spine links every step -->
      <div class="bg-base-200/40 border-base-300 overflow-x-auto rounded-2xl border p-4 sm:p-6">
        <div class="relative mx-auto flex w-full max-w-2xl flex-col">
          <!-- continuous dashed flow line behind the steps -->
          <div
            class="border-base-content/20 pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 border-l border-dashed"
          ></div>
          ${renderInsertZone(0)}
          ${sequence.map((step, i) => {
            const descriptor = descriptors[i];
            if (!descriptor) return nothing;
            const expanded = expandedIdx === i;
            const isLinkedIn = laneOf(step.channel) === 'linkedin';
            const card = SequenceStepCard({
              step,
              descriptor,
              expanded,
              isFirst: i === 0,
              isLast: i === sequence.length - 1,
              offering,
              sampleLead,
              sampleLeadLoading,
              language: props.language,
              linkedinAccountIds: props.linkedinAccountIds,
              previewAiEmployeeId: props.previewAiEmployeeId,
              onToggle: () => toggleExpand(i),
              onRemove: () => removeStep(i),
              onMoveUp: () => moveStep(i, i - 1),
              onMoveDown: () => moveStep(i, i + 1),
              onPatch: patch => patchStep(step.id, patch),
            });
            // One connected flow with the spine down the middle: email steps are left-aligned,
            // LinkedIn steps right-aligned (both SAME width, so the left/right inset is identical).
            // Each card still overlaps the centre, so the dashed line runs into every card top/
            // bottom → connected, not detached. Expanded uses the full width for the editor.
            // Same wrapper template either way so expand/collapse never tears down the step's
            // preview state.
            const wrapperClass = expanded
              ? 'relative z-10 mx-auto w-full max-w-2xl'
              : isLinkedIn
                ? 'relative z-10 ml-auto mr-0 w-full max-w-md'
                : 'relative z-10 mr-auto ml-0 w-full max-w-md';
            return html`
              ${i > 0 ? renderWaitChip(i) : renderImmediatePill()}
              <div class="${wrapperClass}">${card}</div>
              ${renderInsertZone(i + 1)}
            `;
          })}
        </div>
      </div>

      <!-- Reply Settings -->
      <div class="card bg-base-100 border-base-300 mt-6 border shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between">
            <h3 class="card-title gap-2 text-base">
              <span class="icon-[material-symbols--reply] text-xl"></span>
              Auto-Generate Reply Drafts
            </h3>
            <input
              type="checkbox"
              class="toggle toggle-primary"
              ?checked=${!!autoReply}
              @change=${(e: Event) => onAutoReplyChange?.((e.target as HTMLInputElement).checked)}
            />
          </div>
          <p class="text-base-content/60 text-sm">
            When enabled, the AI agent automatically generates draft replies when leads respond to your campaign.
          </p>
          ${
            autoReply
              ? html`
                <div class="mt-3 flex flex-col gap-3">
                  <div>
                    <label class="label"><span class="label-text text-sm font-medium">Reply Prompt</span></label>
                    <prompt-editor
                      data-editor-id="reply-prompt"
                      .value=${replyPrompt ?? ''}
                      .placeholder=${'Describe how the AI should respond when a lead replies...\n\nExample: "Respond professionally. If they are interested, suggest a demo call. If they have questions, answer them helpfully. Keep it under 120 words."'}
                      .rows=${4}
                      @value-changed=${(e: CustomEvent<string>) => onReplyPromptChange?.(e.detail ?? '')}
                    ></prompt-editor>
                    <!--
                      No variable chips here, unlike the outreach steps. A reply is
                      written against what THIS lead actually said, so a fixed set of
                      {{first_name}}-style placeholders doesn't fit the reply prompt —
                      it described a template the reply agent never fills that way.
                    -->
                  </div>
                </div>
              `
              : nothing
          }
        </div>
      </div>
    </div>
  `;
});
